// src/utils/excelStyles.js
// ============================================================================
// Global Excel Export Styling Helper
// ============================================================================
// Provides centralised font styles for all Excel (ExcelJS) exports.
// Styles are configured in AccountingSettingsPage → "Excel Export" tab and
// persisted in the app_settings table under key 'excelExportStyles'.
//
// Usage:
//   import { getExcelStyle, setExcelStyles } from '../utils/excelStyles';
//
//   // At app-init or when settings load, call once:
//   setExcelStyles(settingsHook.excelExportStyles);
//
//   // Then anywhere in export code:
//   cell.font = getExcelStyle('style1');           // { name, size }
//   cell.font = { ...getExcelStyle('style3'), bold: true };
// ============================================================================

const DEFAULT_EXCEL_STYLES = {
  style1: { fontFamily: 'Itcavantgardestd-bk', fontSize: 10 },
  style2: { fontFamily: 'Itcavantgardestd-bk', fontSize: 12 },
  style3: { fontFamily: 'Itcavantgardestd-bk', fontSize: 16 },
};

// Module-level cache – updated via setExcelStyles()
let _styles = { ...DEFAULT_EXCEL_STYLES };

/**
 * Inject the current user-configured styles (call when settings load).
 * @param {object|null|undefined} styles  – excelExportStyles from useSettings
 */
export function setExcelStyles(styles) {
  if (styles && typeof styles === 'object') {
    _styles = { ...DEFAULT_EXCEL_STYLES, ...styles };
  } else {
    _styles = { ...DEFAULT_EXCEL_STYLES };
  }
}

/**
 * Return an ExcelJS-compatible font object for the given style key.
 * @param {'style1'|'style2'|'style3'} key
 * @returns {{ name: string, size: number }}
 */
export function getExcelStyle(key) {
  const s = _styles[key] || DEFAULT_EXCEL_STYLES[key] || DEFAULT_EXCEL_STYLES.style1;
  return { name: s.fontFamily, size: s.fontSize };
}

export { DEFAULT_EXCEL_STYLES };
